import React, { FocusEvent, useEffect, useRef } from 'react';
import { MENU_CLOSE_DELAY, CLICK_RESET_DELAY } from '../../constants';
import { SubNavigation, TabOption } from '../../models/menuModels';
import NavigationItem from '../menu/NavigationItem';
import styles from './TabSubNavigation.module.scss';

type Props = {
    tab: TabOption;
    index: number;
    closeSubMenu: () => void;
    subNavigation: SubNavigation[];
    onBlur: (e: FocusEvent<HTMLElement>, index: number) => void;
};

const TabSubNavigation = ({ tab, subNavigation, closeSubMenu, index, onBlur }: Props): React.ReactElement => {
    const isClickingRef = useRef(false);
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const closeMenu = (): void => {
        setTimeout(() => {
            closeSubMenu();
        }, MENU_CLOSE_DELAY);
    };

    const blur = (e: FocusEvent<HTMLElement>): void => {
        if (isClickingRef.current) {
            return;
        }

        const enterTarget = e.relatedTarget;
        if (enterTarget && e.currentTarget.contains(enterTarget)) {
            return;
        }

        onBlur(e, index);
    };

    const handleMenuItemMouseDown = (): void => {
        isClickingRef.current = true;
    };

    const handleMenuItemTouchStart = (): void => {
        isClickingRef.current = true;
    };

    const handleMenuItemClick = (): void => {
        isClickingRef.current = true;
        closeMenu();

        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
        }

        clickTimeoutRef.current = setTimeout(() => {
            isClickingRef.current = false;
            clickTimeoutRef.current = null;
        }, CLICK_RESET_DELAY);
    };

    useEffect(() => {
        const focusedItem = document.getElementById(subNavigation[0]?.id);
        if (focusedItem) {
            focusedItem.focus();
        }

        return (): void => {
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
            }
        };
    }, [subNavigation]);

    const dropdownPlacement = 90 + 150 * index;
    return (
        <ul
            className={styles.dropdown}
            style={{ left: `${dropdownPlacement}px` }}
            role="menu"
            onBlur={blur}
            onMouseDown={handleMenuItemMouseDown}
            onTouchStart={handleMenuItemTouchStart}
        >
            {subNavigation.map(menuItem => (
                <NavigationItem
                    key={menuItem.id}
                    path={`${tab.path}?subPage=${menuItem.queryParam}`}
                    onClick={handleMenuItemClick}
                    title={menuItem.title}
                    navigationType="large"
                    id={menuItem.id}
                    centered
                />
            ))}
        </ul>
    );
};
export default TabSubNavigation;
